import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { WhiteLabelProvider } from './contexts/WhiteLabelContext';
import PrivateRoute from './components/auth/PrivateRoute';
import PublicRoute from './components/auth/PublicRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoginForm from './components/auth/LoginForm';
import LandingPage from './pages/LandingPage';
import SignupWizard from './pages/SignupWizard';
import AgencySignupPage from './pages/AgencySignupPage';
import Layout from './components/Layout';
import Dashboard from './components/dashboard/Dashboard';
import ClientsListPage from './components/clients/ClientsListPage';
import ClientPage from './components/clients/ClientPage';
import CreateClientPage from './components/clients/CreateClientPage';
import UsersListPage from './components/users/UsersListPage';
import PostsListPage from './components/posts/PostsListPage';
import ArticlesListPage from './components/articles/ArticlesListPage';
import ArticleEditorPage from './components/articles/ArticleEditorPage';
import ScheduledPostsPage from './components/posts/ScheduledPostsPage';
import PostReviewPage from './components/posts/PostReviewPage';
import PostReviewSimpleA from './components/posts/PostReviewSimpleA';
import PostReviewSimpleB from './components/posts/PostReviewSimpleB';
import PostReviewLayoutC from './components/posts/PostReviewLayoutC';
import CreatePostPage from './components/posts/CreatePostPage';
import TrashBinPage from './components/posts/TrashBinPage';
import SuperAdminDashboard from './components/super-admin/SuperAdminDashboard';
import AgenciesListPage from './components/super-admin/AgenciesListPage';
import AgencyDetailPage from './components/super-admin/AgencyDetailPage';
import PlansListPage from './components/super-admin/PlansListPage';
import FeaturesListPage from './components/super-admin/FeaturesListPage';
import ReportsPage from './components/super-admin/ReportsPage';
import PackagesListPage from './components/packages/PackagesListPage';
import DesignRequestsPage from './components/design-requests/DesignRequestsPage';
import DesignRequestDetailsPage from './components/design-requests/DesignRequestDetailsPage';
import MediaLibraryPage from './components/media/MediaLibraryPage';
import PublicPostReviewPage from './components/posts/PublicPostReviewPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SettingsPage from './components/settings/SettingsPage';
import AgencySettingsPage from './components/settings/AgencySettingsPage';
import ArchivedClientsPage from './components/settings/ArchivedClientsPage';
import SocialMediaIntegrations from './components/settings/SocialMediaIntegrations';
import NotificationsPage from './components/notifications/NotificationsPage';
import AgencyTicketsPage from './components/tickets/AgencyTicketsPage';
import TicketsListPage from './components/super-admin/TicketsListPage';
import SeoPlansPage from './components/seo/SeoPlansPage';
import PublicSeoReviewPage from './components/seo/PublicSeoReviewPage';
import SystemSettingsPage from './components/super-admin/SystemSettingsPage';
import PaymentPage from './pages/PaymentPage';

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <WhiteLabelProvider>
            <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route index element={<LandingPage />} />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <SignupWizard />
                </PublicRoute>
              }
            />
            <Route
              path="/signup/agency"
              element={
                <PublicRoute>
                  <AgencySignupPage />
                </PublicRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginForm />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />
            <Route path="/post/:token" element={<PublicPostReviewPage />} />
            <Route path="/seo/public/:token" element={<PublicSeoReviewPage />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} />
            </Route>

            <Route
              path="/clients"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<ClientsListPage />} />
              <Route path="new" element={<CreateClientPage />} />
              <Route path=":id" element={<ClientPage />} />
            </Route>

            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<UsersListPage />} />
            </Route>

            <Route
              path="/packages"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<PackagesListPage />} />
            </Route>

            <Route
              path="/design-requests"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<DesignRequestsPage />} />
              <Route path=":id" element={<DesignRequestDetailsPage />} />
            </Route>

            <Route
              path="/articles"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<ArticlesListPage />} />
              <Route path="new" element={<ArticleEditorPage />} />
              <Route path=":id/edit" element={<ArticleEditorPage />} />
            </Route>

            <Route
              path="/media"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<MediaLibraryPage />} />
            </Route>

            <Route
              path="/posts"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<PostsListPage />} />
              <Route path="scheduled" element={<ScheduledPostsPage />} />
              <Route path="new" element={<CreatePostPage />} />
              <Route path="trash" element={<TrashBinPage />} />
              {/* Experimental layouts for post review */}
              <Route path=":id/layout-a" element={<PostReviewSimpleA />} />
              <Route path=":id/layout-b" element={<PostReviewSimpleB />} />
              <Route path=":id/layout-c" element={<PostReviewLayoutC />} />
              <Route path=":id" element={<PostReviewPage />} />
            </Route>

            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<SettingsPage />} />
              <Route path="agency" element={<AgencySettingsPage />} />
              <Route path="social-media" element={<SocialMediaIntegrations />} />
              <Route path="archived-clients" element={<ArchivedClientsPage />} />
            </Route>

            <Route
              path="/payment"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<PaymentPage />} />
            </Route>

            <Route
              path="/seo"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<SeoPlansPage />} />
            </Route>

            <Route
              path="/tickets"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<AgencyTicketsPage />} />
            </Route>

            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<NotificationsPage />} />
            </Route>

            {/* Super Admin Routes */}
            <Route
              path="/super-admin"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="agencies" element={<AgenciesListPage />} />
              <Route path="agencies/:id" element={<AgencyDetailPage />} />
              <Route path="plans" element={<PlansListPage />} />
              <Route path="features" element={<FeaturesListPage />} />
              <Route path="settings" element={<SystemSettingsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="tickets" element={<TicketsListPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            </BrowserRouter>
          </WhiteLabelProvider>
        </AuthProvider>
      </LanguageProvider>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </ErrorBoundary>
  );
}

export default App;
